/**
 * Le crochet d'une conversation de checkpoint : il branche la machine
 * (`checkpointConversationMachine`) sur l'API, le minuteur de la croix, les sons et la mesure.
 *
 * Le starting block le lit avant le live ; la main levée le lira pendant (lot du live) : rien n'y est
 * propre au démarrage, sauf ce que dit le contexte (`context`) — la réponse part sans activité au
 * démarrage, avec l'activité en cours sinon (`activityId`), comme avant.
 *
 * Ce qui part d'ici :
 *  - chaque réponse (`POST conversations/{id}/response`), la photo d'abord au stockage, vérifiée ;
 *  - le pétale, une fois, au bout (`award-checkpoint-petal`) ;
 *  - l'avis (`feedback`), puis, une seconde après, la fin (`onFinished`) ;
 *  - la fermeture : une conversation encore en cours se dit interrompue (`finish`,
 *    `student_dismiss`) ;
 *  - un fil d'Ariane à chaque transition, une erreur typée à chaque échec — aucun `catch` vide : les
 *    deux `.catch(() => {})` de l'ancien `SamCheckpointOverlay` (le pétale, l'interruption) ne sont
 *    pas recopiés ;
 *  - les événements Amplitude de l'ancien écran (`sam_screen.*` ou `sam_checkpoint_overlay.*`).
 *
 * L'écran joue ce que la machine dit : les sons (`onSound`, par la branche du live ou celle d'avant
 * l'appel), l'appareil photo (`photoRequested`) et le calendrier (`dateRequested`) ; il rend la photo ou
 * la date par `sendPhoto` et `sendDate`.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { createTelemetry, type EngineTelemetry, type TelemetryRole } from "../../shared/telemetry"
import { type PhaseTransition, useSignalReducer, withPhaseTransitions } from "../../shared/useSignalReducer"
import { defaultCheckpointApi, type CheckpointApi } from "./checkpointApi"
import {
    PHOTO_RESPONSE,
    checkpointReducer,
    datedResponse,
    initialCheckpointState,
    isInterruptedOnClose,
    isStartingBlockContext,
    planReply,
    writtenResponse,
    type ApiCheckpointConversation,
    type CheckpointConversationState,
    type CheckpointMessage,
} from "./checkpointConversationMachine"
import { TIMING } from "./constants"
import { useCheckpointTimer } from "./useCheckpointTimer"

const reduceWithTransitions = withPhaseTransitions(
    checkpointReducer,
    (state: CheckpointConversationState) => state.phase
)

/**
 * Les moments sonores d'une conversation de Sam, ceux de l'ancien `SamConversationBase` : l'ouverture, une
 * réponse touchée, le pétale gagné (`PetalRewardBadge`).
 */
export type CheckpointSound = "open" | "select" | "earned"

export type UseCheckpointConversationOptions = {
    /** La conversation telle que le serveur l'a créée (`POST conversations`). */
    conversation: ApiCheckpointConversation
    role: TelemetryRole
    /** `screen` avant le live, `checkpoint_overlay` pendant : le nom des événements Amplitude d'avant. */
    surface: "screen" | "checkpoint_overlay"
    /** L'activité en cours : jointe à chaque réponse hors du démarrage. */
    activityId?: number | string | null
    /** Un moment sonore : l'écran joue le son, par la branche du live pendant un live. */
    onSound?: (sound: CheckpointSound) => void
    /** Chaque tour rendu par le serveur (le live y lit l'activité suggérée et prévient le tuteur). */
    onTurn?: (data: ApiCheckpointConversation) => void
    /** L'avis est donné (une seconde après), ou la fin est choisie : la conversation a fini son travail. */
    onFinished?: () => void
    /** La croix : `completed` si la conversation était allée au bout. */
    onClosed?: (result: { completed: boolean }) => void
    /** Les tests passent les leurs. */
    api?: CheckpointApi
    telemetry?: EngineTelemetry
}

export type CheckpointConversationController = {
    state: CheckpointConversationState
    /** La croix est là : au bout de 10 s, tout de suite au bout ou sur une erreur. */
    canClose: boolean
    /** Une réponse touchée ouvre l'appareil photo : à l'écran de le lancer, puis `sendPhoto`. */
    photoRequested: boolean
    /** Une réponse touchée ouvre le calendrier : à l'écran de le montrer, puis `sendDate`. */
    dateRequested: boolean
    reply: (key: string) => void
    setText: (value: string) => void
    sendText: () => void
    sendDate: (date: string) => void
    sendPhoto: (uri: string) => void
    cancelGesture: () => void
    giveFeedback: (value: 1 | -1) => void
    close: () => void
}

export function useCheckpointConversation(options: UseCheckpointConversationOptions): CheckpointConversationController {
    const { conversation, role } = options
    const context = conversation?.conversation?.trigger_context ?? "starting_block"
    const optionsRef = useRef(options)
    optionsRef.current = options
    const api = options.api ?? defaultCheckpointApi
    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("checkpoint", { role }),
        // La télémétrie d'une conversation montée ne change pas en route.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    // Chaque passage d'une phase à l'autre laisse son fil d'Ariane.
    const [state, dispatch, read] = useSignalReducer(
        reduceWithTransitions,
        () => initialCheckpointState(context),
        (transitions: PhaseTransition[]) => {
            for (const { from, to, event } of transitions) telemetry.transition(from, to, event, { context })
        }
    )
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
    useEffect(() => () => timersRef.current.forEach(clearTimeout), [])

    const track = useCallback(
        (name: string, properties?: Record<string, unknown>) =>
            amplitude.track(`sam_${optionsRef.current.surface}.${name}`, properties),
        []
    )
    const sound = useCallback((which: CheckpointSound) => optionsRef.current.onSound?.(which), [])

    // L'ouverture : une fois par conversation.
    const openedIdRef = useRef<unknown>(null)
    useEffect(() => {
        const id = conversation?.conversation?.id ?? conversation?.agent_message?.conversation_id ?? null
        if (openedIdRef.current === id && id != null) return
        openedIdRef.current = id
        dispatch({ type: "opened", data: conversation })
        track("open")
        sound("open")
        if (read().error === "empty") telemetry.error("open", new Error("conversation sans message"), { context })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation])

    // Le pétale se gagne une fois, au bout.
    const petalRef = useRef(false)
    useEffect(() => {
        const id = state.conversationId
        if (!state.earned || petalRef.current || id == null) return
        petalRef.current = true
        sound("earned")
        api.awardPetal(id).catch((error: unknown) => telemetry.error("award-petal", error, { context }))
    }, [state.earned, state.conversationId, api, telemetry, context, sound])

    const turnRef = useRef(0)
    const send = useCallback(
        (
            message: CheckpointMessage,
            body: { student_response: string; checkpoint_attachments?: string[] },
            photoUri?: string
        ) => {
            const id = read().conversationId
            if (id == null) return
            turnRef.current += 1
            const turn = turnRef.current
            dispatch({ type: "sent", turn, message })
            const activityId = optionsRef.current.activityId
            const withActivity = !isStartingBlockContext(context) && activityId != null ? { activity: activityId } : {}
            void (async () => {
                let attachments: string[] | undefined
                if (photoUri != null) {
                    try {
                        attachments = [await api.uploadPhoto(photoUri)]
                    } catch (error) {
                        telemetry.error("photo-upload", error, { context })
                        dispatch({ type: "responseFailed", turn, kind: "photo" })
                        return
                    }
                }
                try {
                    const data = await api.respond(id, {
                        ...body,
                        ...withActivity,
                        ...(attachments != null ? { checkpoint_attachments: attachments } : {}),
                    })
                    dispatch({ type: "responseReceived", turn, data })
                    if (read().error === "empty") {
                        telemetry.error("respond", new Error("réponse sans message"), { context })
                    }
                    optionsRef.current.onTurn?.(data)
                } catch (error) {
                    telemetry.error("respond", error, { context })
                    dispatch({ type: "responseFailed", turn, kind: "response" })
                }
            })()
        },
        [api, context, dispatch, read, telemetry]
    )

    const close = useCallback(() => {
        const current = read()
        if (current.phase === "closed") return
        track("close")
        const id = current.conversationId
        if (isInterruptedOnClose(current) && id != null) {
            api.finish(id, { interruption_reason: "student_dismiss", ended_at: new Date().toISOString() }).catch(
                (error: unknown) => telemetry.error("finish", error, { context })
            )
        }
        dispatch({ type: "closed" })
        optionsRef.current.onClosed?.({ completed: current.phase === "ended" })
    }, [api, context, dispatch, read, telemetry, track])

    const timerAllows = useCheckpointTimer(
        state.phase === "ended",
        state.phase === "opening" || state.phase === "sending",
        TIMING.CLOSE_BUTTON_DELAY
    ) as boolean

    return {
        state,
        canClose: state.canClose || timerAllows,
        photoRequested: state.gesture === "photo",
        dateRequested: state.gesture === "date",
        reply: useCallback(
            (key: string) => {
                const current = read()
                const chosen = current.replies.find((candidate) => candidate.key === key)
                if (chosen == null || current.phase !== "answering") return
                track("response.click", { action: chosen.action ?? undefined })
                sound("select")
                const plan = planReply(chosen)
                dispatch({ type: "replyChosen", key, plan })
                if (plan.kind === "send") {
                    send(
                        { id: `me-${current.messages.length}`, author: "student", text: plan.display },
                        {
                            student_response: plan.body,
                        }
                    )
                } else if (plan.kind === "activity") {
                    // « Revenir à l'activité » ferme la conversation, comme avant.
                    close()
                }
            },
            [close, dispatch, read, send, sound, track]
        ),
        setText: useCallback((value: string) => dispatch({ type: "textChanged", value }), [dispatch]),
        sendText: useCallback(() => {
            const current = read()
            const chosen = current.replies.find((candidate) => candidate.key === current.chosenKey)
            const text = current.textValue.trim()
            if (chosen == null || text === "") return
            const written = writtenResponse(chosen, text)
            send(
                { id: `me-${current.messages.length}`, author: "student", text: written.display },
                {
                    student_response: written.body,
                }
            )
        }, [read, send]),
        sendDate: useCallback(
            (date: string) => {
                const current = read()
                const chosen = current.replies.find((candidate) => candidate.key === current.chosenKey)
                if (chosen == null) return
                const dated = datedResponse(chosen, date)
                send(
                    { id: `me-${current.messages.length}`, author: "student", text: dated.display },
                    {
                        student_response: dated.body,
                    }
                )
            },
            [read, send]
        ),
        sendPhoto: useCallback(
            (uri: string) => {
                const current = read()
                send(
                    { id: `me-${current.messages.length}`, author: "student", imageUri: uri },
                    { student_response: PHOTO_RESPONSE },
                    uri
                )
            },
            [read, send]
        ),
        cancelGesture: useCallback(() => dispatch({ type: "gestureCancelled" }), [dispatch]),
        giveFeedback: useCallback(
            (value: 1 | -1) => {
                const current = read()
                const id = current.conversationId
                if (current.feedback != null || current.phase !== "ended" || id == null) return
                track("feedback.submit", { value })
                dispatch({ type: "feedbackGiven", value })
                api.feedback(id, value)
                    .catch((error: unknown) => telemetry.error("feedback", error, { context }))
                    .finally(() => dispatch({ type: "feedbackSettled" }))
                // L'avis donné, la conversation a fini son travail : une seconde pour voir le pouce.
                timersRef.current.push(setTimeout(() => optionsRef.current.onFinished?.(), TIMING.FEEDBACK_CLOSE_DELAY))
            },
            [api, context, dispatch, read, telemetry, track]
        ),
        close,
    }
}
